// The SUBPATH, not the barrel: the package index pulls in zod and the whole
// profanity word list, and this page is the top of the install funnel — every
// kilobyte here is paid for by somebody on a phone deciding whether to bother.
// `shareLinks` has no imports of its own.
import { shareLinkSource } from "@fazly/shared/shareLinks";
import { useEffect } from "react";

import { type Store, useTrack } from "../lib/analytics";
// Where someone without the app is sent. One module, because this page and the
// landing page both sell the same app — see lib/stores.ts.
import { appStoreLink, playStoreLink } from "../lib/stores";
import { PageChrome } from "./PageChrome";

/**
 * What a Fazly share link shows to someone who does NOT have the app.
 *
 * ─── WHAT THIS REPLACED, AND WHY IT WAS WRONG ───────────────────────────────
 *
 * Until now this file was `SharedItem.tsx`, served at `/post/<code>` and
 * `/request/<code>`, and it said "Someone shared a prayer request with you".
 * That is Bible Shield — the AKES reference implementation this repo was
 * scaffolded from (CLAUDE.md). Fazly has no community, no posts and no prayer
 * requests; it has a face scan and a referral code. So the site was serving two
 * pages for another product at two paths nothing links to, while the two paths
 * Fazly ACTUALLY claims — `/r/*` and `/scan/*`, declared in
 * `packages/shared/src/shareLinks.ts` and served in the association files by
 * `lib/appLinks.ts` — had no page at all and 404'd.
 *
 * ─── NOTHING IN THE APP PRODUCES THESE LINKS YET. THE PAGES STILL SHIP ──────
 *
 * Checked before writing this, and worth writing down because the obvious
 * reading of "the share routes are missing" is that a share feature is live and
 * broken. It is not:
 *
 *  - `referralLink()` and `scanLink()` have NO call site in `apps/mobile`. The
 *    referral router does not exist (`packages/api/src/root.ts` lists it among
 *    the routers not written yet), so there is nothing to hand out a code with
 *    and nothing to redeem one against.
 *  - There is no shared-scan surface either. v1's share was an IMAGE — a
 *    glow-up card with the seven scores and "Try yours at fazly.app" burned
 *    into it (`recovery/screens/share.png`) — not a URL to a page. `/scan/*`
 *    is declared AHEAD of the build that claims it, for the reason
 *    `shareLinks.ts` states.
 *  - The deep-link side WAS a stub too — `apps/mobile/src/lib/adAttribution.ts`
 *    resolved a OneLink destination and then routed nowhere, guarded by a name
 *    that did not exist. That half is fixed (the app now hands the resolved url
 *    to its own router), so of the three gaps listed here only the first two are
 *    still open: nothing produces a link to hand out.
 *
 * The pages ship anyway, and the ordering is the whole argument — the same one
 * `lib/appLinks.ts` makes about the association files. Both must be LIVE before
 * a build claims the paths, because iOS fetches the AASA once at install and
 * caches the outcome, failures included. A page that appears the week the share
 * button does is a week of links landing on a 404, and a 404 is also what a
 * hand-typed or mis-copied link gets forever.
 *
 * ─── WHY NEITHER PAGE RENDERS WHAT WAS SHARED ───────────────────────────────
 *
 * `SharedItem` refused to render a shared prayer request, and the reasoning
 * transfers intact even though the content does not. A scan is a photograph of
 * somebody's face plus seven numbers scoring it. Putting that on an open web
 * page republishes it to anyone with the URL and to every crawler that follows
 * one — a change to the privacy model that the person who shared it never
 * agreed to and that cannot be taken back once indexed. It is also the single
 * most sensitive thing this product holds.
 *
 * So the page confirms that something was shared, says what kind of thing it
 * is, and offers the app. Somebody who has the app never sees it — iOS and
 * Android hand the URL straight over. The person who does see it is exactly the
 * person the app has to be sold to.
 *
 * `noindex` is on for the same reason, plus one more: a referral code is a
 * per-user identifier, and a crawlable `/r/` space is a directory of them.
 */

/**
 * Which share surface sent this visitor — the `?s=` parameter, written into the
 * link by whatever hands it out and read back with the same rule the app's
 * deep-link parser uses, so a value that reaches PostHog is one we could have
 * written.
 *
 * Read from `window.location` rather than the router because both callers are
 * client-only (an effect and a click handler) and analytics never runs on the
 * Worker — and because posthog-js does NOT put campaign parameters on the event
 * itself: with `person_profiles: "never"` they are neither set on a person nor
 * captured as properties, leaving the parameter visible only inside the
 * `$current_url` string, which no funnel can be broken down by.
 */
function shareSource(): string | null {
  if (typeof window === "undefined") return null;
  return shareLinkSource(window.location.search);
}

export type ShareKind = "referral" | "scan" | "post";

const COPY: Record<ShareKind, { title: string; blurb: string }> = {
  referral: {
    title: "Someone invited you to Fazly",
    // What the app does, in the order it does it, and nothing it does not do.
    // In particular NO REWARD IS PROMISED: the schema has the ledger for one
    // (`User.referralRewardWeeksGranted`) and the server has no router to write
    // it, so a page saying "you both get a free week" would be selling
    // something that cannot be granted. Same discipline the paywall works
    // under — CLAUDE.md, "the wall sells only what the app actually does".
    blurb:
      "Fazly scores your face from a few selfies — overall, symmetry, jawline, skin, eyes, hair and harmony — then picks the one thing worth working on and builds a daily routine around it.",
  },
  scan: {
    title: "Someone shared their face scan",
    blurb:
      "Their scores open inside Fazly. Get the app and you can run your own scan on a few selfies — seven scores, the one area worth working on, and a routine that gets you there.",
  },
  // A community post. The page does not resolve the code or show the post,
  // for the reason it refuses the other two: a post can carry somebody's
  // photographs and their score card, and a public URL that renders them is a
  // way to lift those out of the community they were posted into. What it says
  // instead is what the community IS, in the app's own terms.
  post: {
    title: "Someone shared a post from the Fazly community",
    blurb:
      "It opens inside Fazly. The community is six rooms — forum, knowledge, products, videos, exercises and diet — where people share what is working for their glow-up, plus a live chat. Reading is free.",
  },
};

export function ShareLanding({ kind }: { kind: ShareKind }) {
  const track = useTrack();
  const copy = COPY[kind];

  // Reaching this page at all is the measurable fact: an installed app
  // intercepts the URL before a browser ever sees it, so every one of these is
  // somebody who does not have Fazly yet. `kind` and the surface that produced
  // the link — never the code in the path, which identifies one person's
  // referral or one person's scan, and which this page otherwise refuses to
  // resolve.
  useEffect(() => {
    track("web_share_landing_viewed", { kind, source: shareSource() });
  }, [track, kind]);

  // The surface rides onto the store click too. This is the one event on the
  // page that is an install intent rather than a visit, so without it "which
  // share surface actually brings users" stops one step short of the answer.
  // `share_referral` / `share_scan` — `section()` in lib/stores.ts buckets any
  // `share_*` into one `af_channel` while `af_sub1` keeps which of the two.
  const openStore = (store: Store) => {
    track("web_store_click", {
      button: store,
      from: `share_${kind}`,
      source: shareSource(),
    });
  };

  return (
    <PageChrome title={copy.title}>
      <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink2">
        {copy.blurb}
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <a
          href={appStoreLink(`share_${kind}`)}
          onClick={() => openStore("appstore")}
          className="rim inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          download for iphone
        </a>
        <a
          href={playStoreLink(`share_${kind}`)}
          onClick={() => openStore("play")}
          className="rim inline-flex h-[54px] items-center justify-center bg-surface px-7 font-display text-[17px] font-bold text-ink"
        >
          download for android
        </a>
      </div>

      {/* The one promise this page is allowed to make about what happens next,
          and it is deliberately the smaller one. It is true today: the
          association files and the Android intent filters both claim `/r/*` and
          `/scan/*`, so an installed app takes the link.

          What is NOT said, because it is not true yet: that the link survives
          the install. Someone who taps a store button here loses the code —
          nothing puts a `deep_link_value` on the OneLink, and even if it did,
          `handleDeepLink` in apps/mobile/src/lib/adAttribution.ts resolves a
          destination and then routes nowhere. Deferred deep linking is the
          thing to build alongside the referral router; until it exists, saying
          "your invite will be waiting" is a sentence the product cannot keep. */}
      <p className="mt-7 max-w-2xl text-[15px] leading-relaxed text-ink2">
        Already have Fazly? Open this link again from your phone and it will
        take you straight there.
      </p>

      {/* Only on the scan and post pages. The line is about not republishing somebody's
          face, which is a real answer to a real question there ("where is the
          scan, then?") and a non-sequitur on an invite, where nothing was
          shared but a code. A reassurance nobody asked for reads as a reason to
          start wondering. */}
      {kind === "scan" || kind === "post" ? (
        <div className="brutal mt-12 max-w-2xl px-5 py-4 text-[15px] leading-relaxed text-ink2">
          We don’t put anyone’s face or scores on the open web. A scan stays
          between the person who took it and the people they send it to.
        </div>
      ) : null}
    </PageChrome>
  );
}

/** Head tags every share page needs: a title, and no indexing. */
export function shareLandingHead(kind: ShareKind) {
  return {
    meta: [
      {
        title:
          kind === "referral"
            ? "Your invite to Fazly — Fazly"
            : kind === "post"
              ? "Shared community post — Fazly"
              : "Shared face scan — Fazly",
      },
      // Not a directory of referral codes or of people's scans, however thin
      // each page is.
      { name: "robots", content: "noindex, nofollow" },
    ],
  };
}
